// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import { Modal } from 'components/atoms/Modal';
import { useTranslation } from 'react-i18next';
import styles from './EditUserModal.module.scss';
import { Form } from 'react-final-form';
import { InputDataListField } from 'components/molecules/InputDataListField';
import { validateEmailAddress, validatePasswordField } from 'util/validator';
import { Account } from 'util/accountManagementTypes';
import { Label } from 'components/atoms/Label';
import Toggle from 'components/atoms/Toggle';
import Popover from 'components/atoms/Popover';
import { IconInfo } from 'components/atoms/icons/IconInfo';

export type UserInput = Pick<Account, 'email' | 'mfaEnable'> & {
  password?: string;
};

export const renderMFATooltip = (t: typeof useTranslation) => {
  return (
    <>
      <p className={styles.mfa_tooltip_title}>
        {t('user_security.mfa_tooltip_title')}
      </p>
      <p>{t('user_security.mfa_tooltip_content')}</p>
    </>
  );
};

interface Props {
  onClose: () => void;
  userData: Account;
  onSubmit: (form: UserInput, userId: number | string) => void;
}

const EditUserModal: React.FC<Props> = ({ onClose, userData, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.userModal}>
      <Modal>
        <div className={styles.modalContent}>
          <div className={styles.customTitle}>
            <p className={styles.descriptionTitle}>
              {t('edit_user_modal.title_edit')}
            </p>
            <div className={styles.top} onClick={onClose}>
              <IconCloseTablet />
            </div>
          </div>

          <Form<UserInput>
            initialValues={{
              email: userData.email,
              mfaEnable: userData.mfaEnable,
            }}
            onSubmit={(values) => {
              onSubmit(values, userData.id);
            }}
            mutators={{
              setValue: ([field, value], state, { changeValue }) => {
                changeValue(state, field, () => value);
              },
            }}
            validate={(values) => {
              return {
                password: values.password?.length
                  ? validatePasswordField(
                      t('login.password'),
                      6,
                      values.password
                    )
                  : undefined,
                email: values.mfaEnable
                  ? validateEmailAddress(values.email || '', {})
                  : undefined,
              };
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit} className={styles.formInput}>
                <div className={styles.inputFrame}>
                  <InputDataListField
                    label={t('edit_user_modal.label_password') || ''}
                    placeholder={t('edit_user_modal.label_password') || ''}
                    name="password"
                    type="password"
                    maxlength={20}
                  />

                  <div className={styles.mfaOnOff}>
                    <div className={styles.mfaOnOffLabel}>
                      <Label
                        className={styles.label}
                        text={t('edit_user_modal.label_mfa') || ''}
                        tag={'div'}
                      />

                      <Popover
                        content={renderMFATooltip(t)}
                        arrowContainerConfigs={{
                          arrowColor: '#4d4d4d',
                        }}
                        popUpStyle={{
                          background: '#4d4d4d',
                          maxWidth: '500px',
                          whiteSpace: 'pre-line',
                        }}
                        containerStyle={{
                          zIndex: '100000',
                        }}
                      >
                        <span>
                          <IconInfo />
                        </span>
                      </Popover>
                    </div>

                    <Toggle
                      value={props.values.mfaEnable}
                      setValue={(value) =>
                        props.form.mutators.setValue('mfaEnable', value)
                      }
                      isDarkMode
                    />
                  </div>

                  {props.values.mfaEnable && (
                    <InputDataListField
                      label={t('edit_user_modal.label_email') || ''}
                      placeholder={t('edit_user_modal.label_email') || ''}
                      name="email"
                      required
                      maxlength={100}
                    />
                  )}
                </div>

                <div className={styles.submit}>
                  <button
                    disabled={props.invalid}
                    className={styles.submitButton}
                    type="submit"
                  >
                    {t('edit_user_modal.button_edit')}
                  </button>
                </div>
              </form>
            )}
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default EditUserModal;
